import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { LoadingButton } from "@mui/lab";
import { Box, Button, IconButton, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { AuthContext } from "../../../contexts/AuthContext";
import { DashboardContext } from "../../../contexts/DashboardContext";
import { DashboardsContext } from '../../../contexts/DashboardsContext';
import { getUserEmail } from "../../../helpers/authUser";
import { updateDashboardFunc } from "../../../lib/helper";
import { Dashboard, DashboardNote, DashboardNoteType, RefreshDataEnum } from "../../../types/files";
import { NotesContent } from "../../modals/dashboard-notes/DashboardNoteDialog";
import { CommentaryTitle } from "../dashboard-notes/Commentary";

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        width: 450,
        maxWidth: 500,
        height: 'fit-content',
        backgroundColor: '#ffffff',
        borderRadius: 8,
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        '&:hover': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
        },
    },
    header: {
        background: 'linear-gradient(to right, #f0fdfa, #eff6ff)',
        padding: '16px 24px',
        borderBottom: '1px solid #e5e7eb',
    },
    headerTitle: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 600,
        color: '#0f172a',
    },
    body: {
        padding: 24,
    },
    sectionLabel: {
        fontSize: 11,
        fontWeight: 700,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 8,
    },
    sectionContent: {
        fontSize: 14,
        color: '#1f2937',
        lineHeight: 1.6,
        whiteSpace: 'pre-line',
    },
    commentaryBox: {
        backgroundColor: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 8,
        padding: 16,
        marginBottom: 16,
    },
    commentaryLabel: {
        fontSize: 11,
        fontWeight: 700,
        color: '#92400e',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 8,
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: 16,
        borderTop: '1px solid #e5e7eb',
    },
    decisionLabel: {
        fontSize: 11,
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
    },
    decisionValue: {
        fontSize: 14,
        fontWeight: 700,
        color: '#111827',
        marginLeft: 4,
    },
    inputField: {
        width: '100%',
        "& .MuiOutlinedInput-root": {
            width: '100%',
            padding: 'unset',
            "& > textarea": {
                fontSize: 14,
                color: '#1f2937',
                lineHeight: 1.6,
                padding: 'unset',
            },
        },
    },
    icon: {
        width: 18,
        height: 18,
        padding: 'unset',
        paddingRight: 16,
        "& > .MuiSvgIcon-root": {
            fill: '#64748b',
        }
    },
    cancelButton: {
        borderRadius: 40,
        borderColor: theme.colors.neutral['100'],
        background: theme.colors.neutral['100'],
        color: theme.colors.neutral['500'],
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
    },
    saveButton: {
        borderRadius: 40,
        background: 'linear-gradient(90deg, #17b7a5 0%, #0e9486 100%)',
        color: '#fff !important',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        '&:hover': {
            backgroundColor: theme.colors.primary['500'],
        }
    },
}));

const CommentaryContentCard: React.FC<{
    dashboard: Dashboard,
    newCommentary?: boolean,
    onClose: () => void,
}> = ({ dashboard, newCommentary, onClose }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const { setDashboards } = useContext(DashboardsContext);
    const { isPublicView } = useContext(DashboardContext);

    const [editable, setEditable] = useState<boolean>(false);
    const [saveReady, setSaveReady] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const inputValue = useRef<TextFieldProps>(null);

    const commentaryNote = useMemo(() => dashboard!.notes?.find(note => note.title === CommentaryTitle), [dashboard]);
    const commentaryNoteContent = useMemo(() => (JSON.parse(commentaryNote?.data || '{}') as NotesContent[])?.[1]?.data?.text || '', [commentaryNote]);

    const formattedContent = useMemo(() => {
        if (!commentaryNoteContent) return null;

        // Match labels only at start of string or after newline, followed by ":"
        const labelRegex = /(^|\n)(Commentary|Context|Decision):\s*/gi;
        const matches: RegExpExecArray[] = [];
        let match: RegExpExecArray | null;

        while ((match = labelRegex.exec(commentaryNoteContent)) !== null) {
            matches.push(match);
        }

        // No labels found at start of lines, return plain text
        if (matches.length === 0) {
            return { isPlainText: true, text: commentaryNoteContent };
        }

        const sections: { label: string; text: string }[] = [];

        for (let i = 0; i < matches.length; i++) {
            const m = matches[i];
            const label = m[2]; // The captured label (Commentary|Context|Decision)
            const startOfText = m.index + m[0].length;
            const endOfText = i < matches.length - 1 ? matches[i + 1].index : commentaryNoteContent.length;
            const text = commentaryNoteContent.slice(startOfText, endOfText).trim();
            sections.push({ label, text });
        }

        // Separate into typed sections for DealCard-style rendering
        const commentary = sections.find(s => s.label.toLowerCase() === 'commentary')?.text || '';
        const context = sections.find(s => s.label.toLowerCase() === 'context')?.text || '';
        const decision = sections.find(s => s.label.toLowerCase() === 'decision')?.text || '';

        return { isPlainText: false, sections, commentary, context, decision };
    }, [commentaryNoteContent]);

    const handleSave = useCallback(async (commentary: string) => {
        setLoading(true);

        const noteData = JSON.stringify([
            { type: 'header', data: { level: 2, text: CommentaryTitle, } },
            { type: 'paragraph', data: { text: commentary } },
        ]);
        const newNote: DashboardNote = {
            id: uuidv4(),
            title: CommentaryTitle,
            data: noteData,
            type: DashboardNoteType.External,
            createdBy: getUserEmail(user),
            updatedBy: getUserEmail(user),
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        let oldNotes: DashboardNote[] = dashboard!.notes || [];
        let newNotes: DashboardNote[] = [];

        if (commentaryNote) {
            newNotes = oldNotes.map(note => {
                if (note.id === commentaryNote.id)
                    return newNote;
                return note;
            }) || [];
        } else {
            newNotes = [...oldNotes, newNote];
        }

        const dashboardUpdate: Dashboard = {
            ...dashboard!,
            notes: newNotes,
            refreshData: {
                lastUpdatedBy: 'user',
                shouldRefresh: true,
                modifiedData: RefreshDataEnum.Notes,
            }
        };

        updateDashboardFunc(dashboardUpdate).then(() => {
            setDashboards(prev => prev.map(d => d.id === dashboard.id ? dashboardUpdate : d));
            setLoading(false);
            setSaveReady(false);
            setEditable(false);
        });
        // eslint-disable-next-line
    }, [user, dashboard, commentaryNote]);

    useEffect(() => {
        if (newCommentary) {
            setEditable(true);
            setSaveReady(true);
        }
        // eslint-disable-next-line
    }, []);

    return (
        <Box className={classes.card}>
            {/* Header */}
            <Box className={classes.header}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography className={classes.headerTitle}>Context</Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                        {(!editable && !isPublicView) && (
                            <IconButton className={classes.icon} disableRipple
                                onClick={() => setEditable(true)}>
                                <EditOutlinedIcon />
                            </IconButton>
                        )}
                        <IconButton className={classes.icon} disableRipple
                            onClick={() => onClose()}>
                            <CloseIcon fontSize="small" sx={{ color: '#64748b' }} />
                        </IconButton>
                    </Stack>
                </Stack>
            </Box>

            {/* Body */}
            <Box className={classes.body}>
                {editable ? (
                    <>
                        <TextField variant="outlined"
                            className={classNames('no-border', classes.inputField)}
                            inputProps={{ style: { width: '100%' }, className: 'no-fade' }}
                            defaultValue={commentaryNoteContent}
                            placeholder={!isPublicView ? 'Add your comments here...' : ''}
                            onChange={() => setSaveReady(!!inputValue.current?.value)}
                            inputRef={inputValue}
                            disabled={isPublicView}
                            minRows={3}
                            maxRows={7}
                            multiline
                        />
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" width="100%" sx={{ mt: 2 }}>
                            <Button variant="outlined" size="small"
                                className={classes.cancelButton}
                                onClick={() => setEditable(false)}
                            > Cancel </Button>
                            <LoadingButton variant="contained" size="small"
                                className={classes.saveButton}
                                loading={loading}
                                disabled={!saveReady}
                                onClick={() => handleSave?.(inputValue.current?.value as string)}
                            > Save </LoadingButton>
                        </Stack>
                    </>
                ) : formattedContent ? (
                    formattedContent.isPlainText ? (
                        <Typography className={classes.sectionContent}>
                            {formattedContent.text}
                        </Typography>
                    ) : (
                        <>
                            {/* Context / Deal Overview section */}
                            {formattedContent.context && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography className={classes.sectionLabel}>Deal Overview</Typography>
                                    <Typography className={classes.sectionContent}>
                                        {formattedContent.context}
                                    </Typography>
                                </Box>
                            )}

                            {/* Commentary section in yellow box */}
                            {formattedContent.commentary && (
                                <Box className={classes.commentaryBox}>
                                    <Typography className={classes.commentaryLabel}>Commentary</Typography>
                                    <Typography className={classes.sectionContent}>
                                        {formattedContent.commentary}
                                    </Typography>
                                </Box>
                            )}

                            {/* Decision footer */}
                            {formattedContent.decision !== undefined && (
                                <Box className={classes.footer}>
                                    <Box>
                                        <Typography component="span" className={classes.decisionLabel}>
                                            Decision:
                                        </Typography>
                                        <Typography component="span" className={classes.decisionValue}>
                                            {formattedContent.decision || 'none'}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </>
                    )
                ) : (
                    <Typography sx={{ fontSize: 14, color: '#9ca3af', lineHeight: 1.6 }}>
                        {!isPublicView ? 'Add your comments here...' : ''}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default memo(CommentaryContentCard);
